import type { Permission } from './roles';
import type { WebsiteSectionType } from './website';

export const GYM_MODULE_KEYS = ['classes', 'trainers', 'appointments', 'menu', 'bar', 'campaigns', 'leads', 'equipment', 'targets', 'corporate', 'knowledge', 'tasks', 'recognition', 'recruiting', 'access', 'harvestPromotion', 'team', 'payroll', 'practice'] as const;
export type GymModuleKey = (typeof GYM_MODULE_KEYS)[number];

export interface GymModuleDefinition {
  key: GymModuleKey;
  name: string;
  summary: string;
  /** What staff, members and visitors stop seeing while it is off. */
  hides: readonly string[];
  /** What is kept, so switching it on again brings everything back. */
  keeps: string;
  /** Set on a part of another module: the platform screen shows it beneath that module. */
  partOf?: GymModuleKey;
  /** Modules that must be on for this one to work: switching one of them off switches this one off too. */
  requires: readonly GymModuleKey[];
  /** Permissions only this module grants. Off, nobody at the gym holds them, whatever their role. */
  permissions: readonly Permission[];
  /** Website blocks that show this module's data. */
  blocks: readonly WebsiteSectionType[];
  /** Pages of the gym's public site that show this module's data. */
  paths: readonly string[];
}

export const GYM_MODULES: readonly GymModuleDefinition[] = [
  {
    key: 'classes',
    name: 'Classes and bookings',
    summary: 'Class types, the weekly timetable, sessions, rooms and member bookings.',
    hides: ['Classes, Sessions, Bookings and Rooms in the admin', 'Classes and Bookings in the member area', 'Class and timetable blocks and the Classes link on the gym site', 'Class reminders'],
    keeps: 'Every class, session and booking stays in the database.',
    requires: [],
    permissions: ['classes.read', 'classes.create', 'classes.update', 'classes.delete', 'classes.own', 'bookings.read', 'bookings.create', 'bookings.cancel', 'rooms.manage'],
    blocks: ['CLASSES', 'SCHEDULE'],
    paths: ['/classes'],
  },
  {
    key: 'trainers',
    name: 'Trainers',
    summary: 'The trainer roster, specialities, trainer pages and the trainers on the gym site.',
    hides: ['Trainers and Specialities in the admin, and the trainer’s own page', 'Trainers in the member area', 'The trainers block and the Trainers link on the gym site'],
    keeps: 'Every trainer stays in the database, and classes keep their trainer.',
    requires: [],
    permissions: ['trainers.read', 'trainers.manage', 'trainers.profile'],
    blocks: ['TRAINERS'],
    paths: ['/trainers'],
  },
  {
    key: 'appointments',
    name: 'Appointments and session packages',
    summary: 'One-to-one sessions with a trainer or a member of staff, their weekly hours, and packages of sessions sold as plans.',
    hides: ['Appointments and Appointment types in the admin, and the trainer’s own day', 'Sessions in the member area', 'Session packages in the plan lists', 'Appointment reminders'],
    keeps: 'Every appointment type, weekly hours, appointment and package balance stays in the database.',
    requires: ['trainers'],
    permissions: ['appointments.read', 'appointments.manage', 'appointments.own', 'appointments.book_for_member', 'appointments.book'],
    blocks: [],
    paths: [],
  },
  {
    key: 'menu',
    name: 'Café and shop menu',
    summary: 'The list of drinks, food and products, shown on the site and sold at the bar.',
    hides: ['Menu in the admin', 'The menu block on the gym site'],
    keeps: 'Every menu item and price stays in the database.',
    requires: [],
    permissions: ['menu.read', 'menu.manage'],
    blocks: ['MENU'],
    paths: [],
  },
  {
    key: 'bar',
    name: 'Bar and prepaid balance',
    summary: 'The bar till, stock per branch, suppliers and purchase orders, bar reports and the balance members top up and spend at the bar.',
    hides: ['Bar, Bar stock, Suppliers and Orders in the admin, the bar sales report and the Bar card of the daily report', 'Balance in the member area and Top up at the desk', 'Bar and stock charts'],
    keeps: 'Sales, stock counts and every member’s balance stay in the database. A balance a member holds cannot be spent or seen while the module is off.',
    requires: ['menu'],
    permissions: ['bar.sell', 'bar.refund', 'bar.stock', 'bar.stock.read', 'bar.insights', 'purchasing.read', 'purchasing.receive', 'purchasing.manage', 'wallet.read', 'wallet.top_up', 'wallet.correct'],
    blocks: [],
    paths: [],
  },
  {
    key: 'campaigns',
    name: 'Campaigns and offers',
    summary: 'Sales on the price list, offers for new people, announcements and campaign texts.',
    hides: ['Campaigns in the admin', 'The site banner and offers blocks', 'Offers in the member area'],
    keeps: 'Every campaign stays in the database, and nothing is shown or sent while it is off.',
    requires: [],
    permissions: ['campaigns.read', 'campaigns.manage', 'campaigns.sms', 'campaigns.email'],
    blocks: ['OFFER'],
    paths: [],
  },
  {
    key: 'leads',
    name: 'Leads and follow-ups',
    summary: 'Website enquiries and walk-ins worked through to membership, with follow-up reminders and a conversion report.',
    hides: ['Leads in the admin and the follow-up count on the Dashboard', 'Follow-up reminders', 'The leads report and its charts'],
    keeps: 'Every lead and its timeline stays in the database. Website enquiries are still emailed to the gym but no longer become leads.',
    requires: [],
    permissions: ['leads.read', 'leads.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'equipment',
    name: 'Equipment and maintenance',
    summary: 'The gym’s register of machines, with a QR label on each, and maintenance tickets staff open when something breaks or a service falls due.',
    hides: ['Equipment and Maintenance in the admin', 'Reporting a problem from a machine’s QR label', 'The daily job that opens tickets for planned service'],
    keeps: 'Every machine, ticket and timeline stays in the database. Printed QR labels stop working until the module is switched back on.',
    requires: [],
    permissions: ['equipment.read', 'equipment.manage', 'maintenance.report', 'maintenance.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'targets',
    name: 'Sales targets',
    summary: 'Monthly targets per branch and per staff member, progress against pace, and the sales report.',
    hides: ['Targets in the admin and My targets on the Dashboard and the front desk', 'The Sold by choice when selling at the desk or converting a lead', 'The sales report and its charts'],
    keeps: 'Every target stays in the database, and sales keep recording who made them.',
    requires: [],
    permissions: ['targets.read', 'targets.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'corporate',
    name: 'Corporate clients',
    summary: 'Companies that buy memberships for their staff: quotes, the employee roster, invoices to the company paid by bank transfer, and the overdue list.',
    hides: ['Corporate in the admin', 'Paid by on the member page', 'The corporate client split of the payments, refunds and sales reports'],
    keeps: 'Every client, quote, invoice and roster link stays in the database. Memberships waiting for an unpaid invoice stay pending until the module is switched back on and the invoice is paid or voided.',
    requires: [],
    permissions: ['corporate.read', 'corporate.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'knowledge',
    name: 'Knowledge base',
    summary: 'The staff’s procedures and policies, written in English and Georgian and shown to the roles each article is for, with an optional confirmation that staff have read it.',
    hides: ['Knowledge base in the admin and the staff portal', 'The articles and the list of who has read them'],
    keeps: 'Every article, revision and confirmation stays in the database.',
    requires: [],
    permissions: ['kb.read', 'kb.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'tasks',
    name: 'Shift tasks',
    summary: 'Opening, closing and cleaning checklists per branch, created fresh every day, ticked by whoever is on shift and reviewed by the shift manager.',
    hides: ['Today’s tasks on the front desk and the Dashboard, and Tasks in the admin', 'The daily job that creates each day’s checklists'],
    keeps: 'Every checklist, day and tick stays in the database. Nothing new is created while the module is off.',
    requires: [],
    permissions: ['tasks.read', 'tasks.manage', 'tasks.complete'],
    blocks: [],
    paths: [],
  },
  {
    key: 'recognition',
    name: 'Recognition',
    summary: 'Awards such as employee of the month, shown to staff or on the gym site, and kudos between colleagues.',
    hides: ['Recognition under Team, and the kudos card on the staff home', 'The recognition block and the awards on trainer pages of the gym site', 'The monthly reminder to pick an award'],
    keeps: 'Every award and kudo stays in the database.',
    requires: [],
    permissions: ['recognition.manage', 'kudos.send'],
    blocks: ['RECOGNITION'],
    paths: [],
  },
  {
    key: 'recruiting',
    name: 'Recruiting',
    summary: 'Job openings on the gym site, applications with a CV, a pipeline from application to hire, and hiring a candidate straight into the team.',
    hides: ['Recruiting in the admin', 'The careers block and the Careers page on the gym site, and applying'],
    keeps: 'Every opening, candidate and CV stays in the database. Rejected candidates are still deleted when the gym’s retention period ends, because the module being off does not change how long personal data may be kept.',
    requires: ['team'],
    permissions: ['recruiting.read', 'recruiting.manage', 'recruiting.interview'],
    blocks: ['CAREERS'],
    paths: ['/careers'],
  },
  {
    key: 'access',
    name: 'Door access control',
    summary: 'Turnstile and door readers, the live door feed, the on-site agent and the old system’s card holders.',
    hides: ['Access control in the admin and Access in the member area', 'The access-control integration', 'Opening a door from the admin'],
    keeps: 'Readers, credentials and every recorded entry stay in the database. Checking a member in at the desk still works.',
    requires: [],
    permissions: ['access.read', 'access.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'harvestPromotion',
    name: 'Promoting old card holders to members',
    summary: 'Staff turn the card holders collected from the gym’s old system into members, with their old membership, in Access control → From the old system.',
    partOf: 'access',
    hides: ['The Promote button and the selection boxes on the From the old system tab; the list of collected holders stays'],
    keeps: 'The on-site agent keeps collecting taps, every collected holder stays listed, and members already promoted stay members.',
    requires: ['access'],
    permissions: [],
    blocks: [],
    paths: [],
  },
  {
    key: 'team',
    name: 'Team, leave and shifts',
    summary: 'Employees, departments, leave requests and approvals, timesheets, shift schedules and onboarding checklists.',
    hides: ['The whole Team section of the admin', 'Leave, Approvals, My leave and pay, Schedule and Timesheets', 'Onboarding checklists and My onboarding on the home page'],
    keeps: 'Every employee, leave record, timesheet and shift stays in the database.',
    requires: [],
    permissions: ['employees.read', 'employees.manage', 'leave.request', 'leave.read', 'leave.approve', 'leave.manage', 'timesheets.own', 'timesheets.clock', 'timesheets.manage', 'shifts.own', 'shifts.manage', 'onboarding.own', 'onboarding.manage', 'reviews.own', 'reviews.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'payroll',
    name: 'Payroll',
    summary: 'Monthly pay runs, pay terms, payslips and the bank salary file.',
    hides: ['Payroll and pay terms in the admin', 'Payslips in My leave and pay'],
    keeps: 'Every run, payslip and pay term stays in the database.',
    requires: ['team'],
    permissions: ['payroll.read', 'payroll.manage', 'payroll.approve', 'employees.pay.read', 'employees.pay.manage'],
    blocks: [],
    paths: [],
  },
  {
    key: 'practice',
    name: 'Practice gym',
    summary: 'A made-up copy of the gym where new staff train.',
    hides: ['The Practice entry in the sidebar and the gym switcher'],
    keeps: 'The practice gym and everything in it stays until someone resets it.',
    requires: [],
    permissions: ['practice.manage'],
    blocks: [],
    paths: [],
  },
];

const BY_KEY = new Map<GymModuleKey, GymModuleDefinition>(GYM_MODULES.map((m) => [m.key, m]));

export function gymModule(key: GymModuleKey): GymModuleDefinition {
  return BY_KEY.get(key)!;
}

export function isGymModuleKey(value: string): value is GymModuleKey {
  return BY_KEY.has(value as GymModuleKey);
}

/** The switched-off modules plus every module that depends on one of them. A key nothing knows is ignored. */
export function effectiveDisabledModules(disabled: Iterable<string> | null | undefined): ReadonlySet<GymModuleKey> {
  const off = new Set<GymModuleKey>();
  for (const key of disabled ?? []) if (isGymModuleKey(key)) off.add(key);
  let grew = true;
  while (grew) {
    grew = false;
    for (const m of GYM_MODULES) {
      if (!off.has(m.key) && m.requires.some((r) => off.has(r))) {
        off.add(m.key);
        grew = true;
      }
    }
  }
  return off;
}

export function permissionsOfModules(keys: Iterable<GymModuleKey>): ReadonlySet<Permission> {
  const out = new Set<Permission>();
  for (const key of keys) for (const p of gymModule(key).permissions) out.add(p);
  return out;
}

export function pathsOfModules(keys: Iterable<GymModuleKey>): readonly string[] {
  return [...keys].flatMap((key) => gymModule(key).paths);
}

export function blocksOfModules(keys: Iterable<GymModuleKey>): ReadonlySet<WebsiteSectionType> {
  const out = new Set<WebsiteSectionType>();
  for (const key of keys) for (const b of gymModule(key).blocks) out.add(b);
  return out;
}
